import { formatCurrency } from '../../../../lib/formatters';
import type { StatementSummary as SummaryType } from '../useAccountStatement';

interface StatementSummaryProps {
  summary: SummaryType;
  currency?: string;
  loading?: boolean;
}

export function StatementSummary({ summary, currency = 'BRL', loading = false }: StatementSummaryProps) {
  const net = summary.net;

  const cards = [
    {
      label: 'Receitas recebidas',
      sub: 'entradas pagas/recebidas',
      value: summary.totalReceived,
      display: `+${formatCurrency(summary.totalReceived, currency)}`,
      icon: 'ri-arrow-down-line',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      valueColor: 'text-emerald-600',
    },
    {
      label: 'Despesas pagas',
      sub: 'saídas com status pago',
      value: summary.totalPaid,
      display: `-${formatCurrency(summary.totalPaid, currency)}`,
      icon: 'ri-arrow-up-line',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-400',
      valueColor: 'text-red-500',
    },
    {
      label: 'Resultado líquido',
      sub: net >= 0 ? 'saldo positivo no período' : 'saldo negativo no período',
      value: net,
      display: `${net >= 0 ? '+' : ''}${formatCurrency(net, currency)}`,
      icon: net >= 0 ? 'ri-funds-line' : 'ri-funds-box-line',
      iconBg: net >= 0 ? 'bg-teal-50' : 'bg-orange-50',
      iconColor: net >= 0 ? 'text-teal-500' : 'text-orange-500',
      valueColor: net >= 0 ? 'text-teal-600' : 'text-orange-500',
    },
    {
      label: 'A pagar/receber',
      sub: `${summary.countPending} lançamento${summary.countPending !== 1 ? 's' : ''} pendente${summary.countPending !== 1 ? 's' : ''}`,
      value: summary.totalPending,
      display: formatCurrency(summary.totalPending, currency),
      icon: 'ri-time-line',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      valueColor: 'text-amber-600',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 animate-pulse">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-gray-100 rounded-lg" />
              <div className="w-28 h-4 bg-gray-100 rounded" />
            </div>
            <div className="w-24 h-6 bg-gray-100 rounded mb-1" />
            <div className="w-32 h-3 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(card => (
        <div key={card.label} className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-7 h-7 ${card.iconBg} rounded-lg flex items-center justify-center`}>
              <i className={`${card.icon} ${card.iconColor} text-sm`} />
            </div>
            <span className="text-xs text-gray-400 font-500">{card.label}</span>
          </div>
          <p className={`text-lg font-700 ${card.valueColor}`}>{card.display}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">{card.sub}</p>
        </div>
      ))}
    </div>
  );
}
